import { useMemo, useState } from "react"
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  Group,
  Loader,
  Modal,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { Trans, useTranslation } from "react-i18next"
import { PageHeader, PageLayout } from "../../../components/crud"
import { useUser } from "../../user/hooks/useUser"
import { usePermissionsList, useDeletePermission } from "../hooks/usePermissions"
import type { PermissionTreeNode } from "../models/permission"
import { getErrorMessage } from "../utils"
import PermissionFormContent from "./PermissionFormContent"

export default function PermissionList() {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.managePermissions ?? false

  const { data: permData, isLoading } = usePermissionsList()
  const deleteMutation = useDeletePermission()

  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState("")

  const [createOpened, setCreateOpened] = useState(false)
  const [editTarget, setEditTarget] = useState<PermissionTreeNode | null>(null)

  const treeNodes = useMemo(() => permData?.items ?? [], [permData])

  const handleDelete = (node: PermissionTreeNode) => {
    if (node.used_by && node.used_by.length > 0) {
      notifications.show({
        color: "red",
        title: t("common.error"),
        message: t("adminPermissions.cannotDeleteInUse", { roles: node.used_by.join(", ") }),
      })
      return
    }

    modals.openConfirmModal({
      title: t("adminPermissions.confirmDelete"),
      children: (
        <Text size="sm">
          {t("adminPermissions.confirmDeleteMessage", { name: node.name })}
        </Text>
      ),
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deleteMutation.mutate(node.name, {
          onSuccess: () => {
            notifications.show({
              color: "green",
              title: t("common.success"),
              message: t("adminPermissions.deleteSuccess"),
            })
          },
          onError: (err) => {
            notifications.show({
              color: "red",
              title: t("common.error"),
              message: getErrorMessage(err),
            })
          },
        })
      },
    })
  }

  const matchesSearch = (node: PermissionTreeNode): boolean => {
    if (!search) return true
    const q = search.toLowerCase()
    if (node.name.toLowerCase().includes(q)) return true
    if (node.description?.toLowerCase().includes(q)) return true
    if (node.children) return node.children.some(matchesSearch)
    return false
  }

  const filteredNodes = useMemo(
    () => (search ? treeNodes.filter(matchesSearch) : treeNodes),
    [treeNodes, search],
  )

  return (
    <PageLayout
      header={
        <PageHeader
          title={t("adminPermissions.title")}
          actions={
            <Group>
              {canManage && (
                <Button onClick={() => setCreateOpened(true)} leftSection={<i className="fa fa-plus" />}>
                  <Trans i18nKey="adminPermissions.create" />
                </Button>
              )}
            </Group>
          }
        />
      }
    >
      <Group mb="md">
        <TextInput
          placeholder={t("adminPermissions.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          leftSection={<i className="fa fa-search" style={{ fontSize: 14 }} />}
          style={{ flex: 1, maxWidth: 400 }}
        />
      </Group>

      {isLoading ? (
        <Box
          style={{
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "var(--mantine-radius-md)",
            overflow: "hidden",
          }}
        >
          {Array.from({ length: 5 }).map((_, i) => (
            <Group key={i} gap="xs" px="md" py="sm" wrap="nowrap" style={{ borderBottom: i < 4 ? "1px solid var(--mantine-color-gray-2)" : undefined }}>
              <Box style={{ width: 28, flexShrink: 0 }}>
                <Skeleton circle h={12} w={12} />
              </Box>
              <Skeleton height={16} width="25%" radius="sm" />
              <Skeleton height={16} width="35%" radius="sm" />
              <Skeleton height={20} width={80} radius="xl" />
              <Skeleton height={20} width={80} radius="xl" />
              <Skeleton circle h={24} w={24} />
              <Skeleton circle h={24} w={24} />
            </Group>
          ))}
        </Box>
      ) : (
        <Box
          style={{
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "var(--mantine-radius-md)",
            overflow: "hidden",
          }}
        >
          {filteredNodes.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="xl">
              <Stack align="center" gap="xs">
                <Text size="40px" c="gray.3">
                  <i className="fa fa-key" />
                </Text>
                {t("adminPermissions.empty")}
              </Stack>
            </Text>
          ) : (
            <Stack gap={0}>
              {filteredNodes.map((node) => (
                <PermissionTreeRow
                  key={node.name}
                  node={node}
                  expanded={expanded}
                  setExpanded={setExpanded}
                  onEdit={setEditTarget}
                  onDelete={handleDelete}
                  canManage={canManage}
                  matchesSearch={matchesSearch}
                  search={search}
                  depth={0}
                />
              ))}
            </Stack>
          )}
        </Box>
      )}

      <Modal
        opened={createOpened}
        onClose={() => setCreateOpened(false)}
        title={t("adminPermissions.createTitle")}
        centered
      >
        <PermissionFormContent onSuccess={() => setCreateOpened(false)} />
      </Modal>

      <Modal
        opened={!!editTarget}
        onClose={() => setEditTarget(null)}
        title={t("adminPermissions.editTitle")}
        centered
      >
        {editTarget && (
          <PermissionFormContent permission={editTarget} onSuccess={() => setEditTarget(null)} />
        )}
      </Modal>
    </PageLayout>
  )
}

interface PermissionTreeRowProps {
  node: PermissionTreeNode
  expanded: Set<string>
  setExpanded: (updater: (prev: Set<string>) => Set<string>) => void
  onEdit: (node: PermissionTreeNode) => void
  onDelete: (node: PermissionTreeNode) => void
  canManage: boolean
  matchesSearch: (node: PermissionTreeNode) => boolean
  search: string
  depth: number
}

function PermissionTreeRow({
  node,
  expanded,
  setExpanded,
  onEdit,
  onDelete,
  canManage,
  matchesSearch,
  search,
  depth,
}: PermissionTreeRowProps) {
  const { t } = useTranslation()
  const hasChildren = node.children && node.children.length > 0
  const isExpanded = expanded.has(node.name)

  const visibleChildren = useMemo(
    () => (search ? node.children?.filter(matchesSearch) ?? [] : node.children ?? []),
    [node.children, search],
  )

  if (hasChildren && visibleChildren.length === 0 && search) {
    return null
  }

  return (
    <Box>
      <Group
        gap="xs"
        px="md"
        py="sm"
        wrap="nowrap"
        style={{
          borderBottom: "1px solid var(--mantine-color-gray-2)",
          backgroundColor: depth === 0 ? "var(--mantine-color-gray-0)" : undefined,
        }}
      >
        <Box style={{ width: depth * 24, flexShrink: 0 }} />
        {hasChildren ? (
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() =>
              setExpanded((prev) => {
                const next = new Set(prev)
                if (next.has(node.name)) next.delete(node.name)
                else next.add(node.name)
                return next
              })
            }
          >
            <i
              className="fa fa-chevron-right"
              style={{
                fontSize: 10,
                transition: "transform 0.15s ease",
                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
              }}
            />
          </ActionIcon>
        ) : (
          <Box style={{ width: 28, flexShrink: 0 }} />
        )}
        <Text size="sm" fw={hasChildren ? 600 : 400} style={{ flex: 1 }}>
          {node.name}
        </Text>
        <Box style={{ flex: 2 }}>
          <Text size="sm" c="dimmed" lineClamp={1}>
            {node.description ?? "—"}
          </Text>
        </Box>
        <Box style={{ flex: 1 }}>
          <Group gap={4}>
            {node.used_by?.map((r) => (
              <Badge key={r} size="sm" variant="light" color="blue">
                {r}
              </Badge>
            ))}
            {(!node.used_by || node.used_by.length === 0) && (
              <Text size="xs" c="dimmed">
                —
              </Text>
            )}
          </Group>
        </Box>
        {canManage && (
          <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
            <Tooltip label={t("common.edit")}>
              <ActionIcon variant="subtle" size="sm" onClick={() => onEdit(node)}>
                <i className="fa fa-pencil" />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={t("common.delete")}>
              <ActionIcon variant="subtle" color="red" size="sm" onClick={() => onDelete(node)}>
                <i className="fa fa-trash" />
              </ActionIcon>
            </Tooltip>
          </Group>
        )}
      </Group>
      {hasChildren && (
        <Collapse in={isExpanded || !!search}>
          {visibleChildren.map((child) => (
            <PermissionTreeRow
              key={child.name}
              node={child}
              expanded={expanded}
              setExpanded={setExpanded}
              onEdit={onEdit}
              onDelete={onDelete}
              canManage={canManage}
              matchesSearch={matchesSearch}
              search={search}
              depth={depth + 1}
            />
          ))}
        </Collapse>
      )}
    </Box>
  )
}
